import { get, groupBy, isEmpty, minBy, orderBy } from 'lodash';

import { fetchBrandCitiesApi, fetchOutletDetailsApi } from '../../../api/api';
import { outletDetailsSize, orderModes, restaurantDataDepthParams } from '../../../constants/globals';
import { appOrderingStatusCodes } from '../../Rewards/config';
import { checkDistanceBetweenCoordinates } from '../../../utils/helpers';
import {
  findNearestOutlet,
  getDeliveryLocationLabel,
  getOutletDisplayName,
  normalizeGuestLocation,
  resolveFirstServiceScanCode,
} from './siteOutletHelpers';
import { isLocationServicableFromValidateResponse } from './siteDeliveryLocation';

const OUTLET_CATEGORY = {
  STOREFRONT: 'STOREFRONT',
  DARK: 'DARK',
};

/** Same-session memo for brand city / outletDetails lists (not geo/reverse-geocode). */
const brandListMemo = {
  darkCities: new Map(),
  darkOutletsByCity: new Map(),
};

export const clearBrandListMemo = () => {
  brandListMemo.darkCities.clear();
  brandListMemo.darkOutletsByCity.clear();
};

export const buildBrandCitiesPath = (brandId, outletCategories) => {
  const base = `/brands/cities/${brandId}`;
  if (isEmpty(outletCategories)) {
    return base;
  }
  const value = Array.isArray(outletCategories) ? outletCategories.join(',') : outletCategories;
  return `${base}?outletCategories=${value}`;
};

export const buildDarkOutletDetailsPath = ({ brandId, cityId, size = 1 }) =>
  `/brands/${brandId}/outletDetails?cityId=${cityId}&outletCategories=${OUTLET_CATEGORY.DARK}&size=${size}&depth=${restaurantDataDepthParams.delivery}`;

export const buildStorefrontAutoSelectPath = (brandId) => {
  const { config, phoneNumbers, workhours } = restaurantDataDepthParams;
  return `/brands/${brandId}/outletDetails?size=${outletDetailsSize}&depth=${config},${phoneNumbers},${workhours}&appOrderingStatus=${appOrderingStatusCodes.active}`;
};

export const buildPickupOutletListPath = ({ brandId, page = 1, size = 100, cityId } = {}) => {
  let url = `/brands/${brandId}/outletDetails?page=${page}&size=${size}&appOrderingStatus=${appOrderingStatusCodes.active}`;
  if (!isEmpty(cityId)) {
    url += `&cityId=${cityId}`;
  }
  return url;
};

export const getCityId = (city) => get(city, 'id', '') || get(city, 'cityId', '');

const getCityLatitude = (city) =>
  get(city, 'defaultLatitude', get(city, 'latitude', get(city, 'lat')));

const getCityLongitude = (city) =>
  get(city, 'defaultLongitude', get(city, 'longitude', get(city, 'lng')));

/** Nearest brand city by Haversine distance to city center. */
export const findNearestCity = (cities, guestLocation) => {
  const normalized = normalizeGuestLocation(guestLocation);
  if (!normalized || isEmpty(cities)) {
    return null;
  }

  const citiesWithCoords = (cities || []).filter((city) => {
    const cityLat = Number(getCityLatitude(city));
    const cityLng = Number(getCityLongitude(city));
    return !Number.isNaN(cityLat) && !Number.isNaN(cityLng);
  });

  if (isEmpty(citiesWithCoords)) {
    return null;
  }

  return minBy(citiesWithCoords, (city) =>
    checkDistanceBetweenCoordinates(
      {
        latitude1: normalized.lat,
        longitude1: normalized.lng,
        latitude2: Number(getCityLatitude(city)),
        longitude2: Number(getCityLongitude(city)),
      },
      false,
    ),
  );
};

export const resolveDarkCity = (cities, guestLocation) => {
  const citiesWithId = (cities || []).filter((city) => !isEmpty(getCityId(city)));
  if (isEmpty(citiesWithId)) {
    return null;
  }
  const nearest = findNearestCity(citiesWithId, guestLocation);
  if (!isEmpty(nearest)) {
    return nearest;
  }
  return citiesWithId[0];
};

export const groupCitiesByInitial = (cities = []) => {
  const orderedCities = Object.entries(
    groupBy(cities, (city) => {
      const name = get(city, 'name', '');
      return name ? String(name)[0].toUpperCase() : '#';
    }),
  ).map(([title, grouped]) => ({ title, cities: grouped }));
  return orderBy(orderedCities, (group) => group.title.toLowerCase());
};

export const MODE_CHIP_ACTIONS = {
  apply: 'apply',
  openPickupOutlets: 'openPickupOutlets',
  startDelivery: 'startDelivery',
  openDarkCities: 'openDarkCities',
};

export const resolveModeChipAction = ({ service, darkStoreDeliveryEnabled }) => {
  const code = get(service, 'code', '');
  const scanCode = get(service, 'scanCode', '');
  if (!isEmpty(scanCode)) {
    return { action: MODE_CHIP_ACTIONS.apply, service };
  }
  if (code === orderModes.selfPickup) {
    return { action: MODE_CHIP_ACTIONS.openPickupOutlets, service };
  }
  if (code === orderModes.delivery && darkStoreDeliveryEnabled) {
    return { action: MODE_CHIP_ACTIONS.startDelivery, service };
  }
  return { action: MODE_CHIP_ACTIONS.apply, service };
};

export const resolveDisplayOrderModeChips = ({
  displayOrderModes,
  serviceTypes,
  applicableOrderModes,
  darkStoreDeliveryEnabled,
}) => {
  const outletServices = isEmpty(applicableOrderModes)
    ? serviceTypes || []
    : (serviceTypes || []).filter((serviceType) =>
        applicableOrderModes.includes(get(serviceType, 'code')),
      );

  if (!darkStoreDeliveryEnabled || isEmpty(displayOrderModes)) {
    return outletServices;
  }

  return displayOrderModes
    .map((code) => {
      const matched = (serviceTypes || []).find((serviceType) => get(serviceType, 'code') === code);
      if (matched) {
        return matched;
      }
      return { code, scanCode: '' };
    })
    .filter((serviceType) => {
      if (isEmpty(applicableOrderModes)) {
        return true;
      }
      return applicableOrderModes.includes(get(serviceType, 'code'));
    });
};

export const fetchDarkCities = async (brandId) => {
  if (isEmpty(brandId)) {
    return [];
  }
  if (brandListMemo.darkCities.has(brandId)) {
    return brandListMemo.darkCities.get(brandId);
  }
  try {
    const response = await fetchBrandCitiesApi({
      brandId,
      outletCategories: OUTLET_CATEGORY.DARK,
    });
    const cities =
      get(response, 'data.cities', null) ||
      get(response, 'cities', null) ||
      [];
    brandListMemo.darkCities.set(brandId, cities);
    return cities;
  } catch (error) {
    return [];
  }
};

export const fetchDarkOutletsForCity = async ({ brandId, cityId }) => {
  if (isEmpty(brandId) || isEmpty(cityId)) {
    return [];
  }
  const memoKey = `${brandId}:${cityId}`;
  if (brandListMemo.darkOutletsByCity.has(memoKey)) {
    return brandListMemo.darkOutletsByCity.get(memoKey);
  }
  try {
    const response = await fetchOutletDetailsApi({
      url: buildDarkOutletDetailsPath({
        brandId,
        cityId,
        size: outletDetailsSize,
      }),
    });
    const outlets = get(response, 'data._embedded.restaurants', []) || [];
    brandListMemo.darkOutletsByCity.set(memoKey, outlets);
    return outlets;
  } catch (error) {
    return [];
  }
};

export const fetchDarkOutletForCity = async ({ brandId, cityId }) => {
  if (isEmpty(brandId) || isEmpty(cityId)) {
    return {};
  }
  try {
    const response = await fetchOutletDetailsApi({
      url: buildDarkOutletDetailsPath({ brandId, cityId }),
    });
    const outlets = get(response, 'data._embedded.restaurants', []) || [];
    return outlets[0] || {};
  } catch (error) {
    return {};
  }
};

export const pickNearestDarkOutlet = (outlets, guestLocation) => {
  if (isEmpty(outlets)) {
    return {};
  }
  const nearest = findNearestOutlet({ outlets, guestLocation });
  const outlet = get(nearest, 'outlet', {});
  if (!isEmpty(get(outlet, 'restaurantCode'))) {
    return outlet;
  }
  return outlets[0] || {};
};

export const bindOutletForPreferredMode = async ({
  outlet,
  fetchRestaurantMenu,
  applicableOrderModes,
  preferredMode,
}) => {
  const restaurantCode = get(outlet, 'restaurantCode', '');
  if (isEmpty(restaurantCode) || !fetchRestaurantMenu) {
    return { scanCode: '', outlet };
  }

  const menuResponse = await fetchRestaurantMenu(restaurantCode, false);
  const serviceTypes = get(menuResponse, 'serviceTypes', []);
  const preferredServices = preferredMode
    ? serviceTypes.filter((serviceType) => get(serviceType, 'code') === preferredMode)
    : serviceTypes;
  const fallbackScanCode = get(outlet, 'restaurantConfig.defaultScanCode', '') || restaurantCode;
  const scanCode = resolveFirstServiceScanCode({
    serviceTypes: !isEmpty(preferredServices) ? preferredServices : serviceTypes,
    applicableOrderModes,
    fallbackScanCode,
  });

  if (!isEmpty(scanCode)) {
    await fetchRestaurantMenu(scanCode, true);
  }

  return { scanCode, outlet, serviceTypes };
};

export const resolveDarkKitchenCandidate = async ({ brandId, guestLocation, cityId: forcedCityId }) => {
  const cities = await fetchDarkCities(brandId);
  if (isEmpty(cities)) {
    return { cities: [], city: null, outlet: {} };
  }

  let city = null;
  if (!isEmpty(forcedCityId)) {
    city = cities.find((c) => getCityId(c) === forcedCityId) || null;
  }
  if (isEmpty(city)) {
    city = resolveDarkCity(cities, guestLocation);
  }

  const cityId = getCityId(city);
  if (isEmpty(city) || isEmpty(cityId)) {
    return { cities, city: null, outlet: {} };
  }

  const outlets = await fetchDarkOutletsForCity({ brandId, cityId });
  const outlet = pickNearestDarkOutlet(outlets, guestLocation);
  if (isEmpty(outlet) || isEmpty(get(outlet, 'restaurantCode'))) {
    return { cities, city, outlet: {} };
  }

  return { cities, city, outlet };
};

export const validateCoordsForOutlet = async ({
  validateLocalities,
  restaurantId,
  lat,
  lng,
}) => {
  if (!validateLocalities || isEmpty(restaurantId) || lat == null || lng == null) {
    return false;
  }
  try {
    const response = await validateLocalities({
      restaurantId,
      locationCoordinates: [{ latitude: lat, longitude: lng }],
    });
    return isLocationServicableFromValidateResponse(response);
  } catch (e) {
    return false;
  }
};

export const resolveDarkKitchenForGuest = async ({
  brandId,
  guestLocation,
  fetchRestaurantMenu,
  applicableOrderModes,
  validateLocalities,
  bind = true,
}) => {
  const { cities, city, outlet } = await resolveDarkKitchenCandidate({
    brandId,
    guestLocation,
  });

  if (isEmpty(outlet) || isEmpty(get(outlet, 'restaurantCode'))) {
    return { cities, city, outlet: {}, scanCode: '', isServicable: false };
  }

  const normalized = normalizeGuestLocation(guestLocation);
  let isServicable = false;
  if (normalized && validateLocalities) {
    isServicable = await validateCoordsForOutlet({
      validateLocalities,
      restaurantId: get(outlet, 'id', '') || get(outlet, 'restaurantId', ''),
      lat: normalized.lat,
      lng: normalized.lng,
    });
  }

  if (!bind || !isServicable) {
    return { cities, city, outlet, scanCode: '', isServicable };
  }

  const { scanCode } = await bindOutletForPreferredMode({
    outlet,
    fetchRestaurantMenu,
    applicableOrderModes,
    preferredMode: orderModes.delivery,
  });

  return { cities, city, outlet, scanCode, isServicable: true };
};

export const bindActiveStorefrontForGuest = async ({
  brandId,
  guestLocation,
  fetchRestaurantMenu,
  applicableOrderModes,
  preferredMode = orderModes.selfPickup,
}) => {
  if (isEmpty(brandId) || !fetchRestaurantMenu) {
    return { outlet: {}, scanCode: '' };
  }

  try {
    const response = await fetchOutletDetailsApi({
      url: buildStorefrontAutoSelectPath(brandId),
    });
    const outlets = get(response, 'data._embedded.restaurants', []) || [];
    if (isEmpty(outlets)) {
      return { outlet: {}, scanCode: '' };
    }

    let outlet = get(findNearestOutlet({ outlets, guestLocation }), 'outlet', {});
    if (isEmpty(get(outlet, 'restaurantCode'))) {
      outlet = outlets[0] || {};
    }
    if (isEmpty(get(outlet, 'restaurantCode'))) {
      return { outlet: {}, scanCode: '' };
    }

    const { scanCode } = await bindOutletForPreferredMode({
      outlet,
      fetchRestaurantMenu,
      applicableOrderModes,
      preferredMode,
    });

    return { outlet, scanCode };
  } catch (e) {
    return { outlet: {}, scanCode: '' };
  }
};

export const getSiteLocationLabel = ({
  restaurant,
  selectedTable,
  isDeliveryMode,
}) => {
  const outlet = !isEmpty(restaurant) ? restaurant : selectedTable;
  if (isDeliveryMode) {
    return getDeliveryLocationLabel(outlet);
  }
  return getOutletDisplayName(outlet);
};
